import { Audio } from '../../shared/audio';
import {
  MAX_NAME_LENGTH,
  PLAYER_ICONS,
  type NewHighScoreInput,
  type PlayerIconId,
} from '../../shared/highScores';

export interface HighScoreCaptureOptions {
  score: number;
  hits: number;
  bestCombo: number;
  onSave(entry: NewHighScoreInput): void;
  onSkip(): void;
}

export interface HighScoreCapture {
  element: HTMLDivElement;
  destroy(): void;
}

const NAME_FILTER_RE = /[^a-zA-Z]/g;

export function createHighScoreCapture(opts: HighScoreCaptureOptions): HighScoreCapture {
  const { score, hits, bestCombo, onSave, onSkip } = opts;
  let selectedIcon: PlayerIconId = PLAYER_ICONS[0].id;
  let typedName = '';

  const root = document.createElement('div');
  root.className = 'capture-overlay overlay';
  root.dataset.testid = 'high-score-capture';

  const modal = document.createElement('div');
  modal.className = 'capture-modal modal';

  const banner = document.createElement('div');
  banner.className = 'capture-banner headline wiggle';
  banner.textContent = '★ NEW HIGH SCORE ★';
  modal.appendChild(banner);

  const headline = document.createElement('h1');
  headline.className = 'capture-headline headline';
  headline.textContent = `${score} points!`;
  modal.appendChild(headline);

  const prompt = document.createElement('p');
  prompt.className = 'capture-prompt';
  prompt.textContent = 'Pick your icon to save your score!';
  modal.appendChild(prompt);

  const grid = document.createElement('div');
  grid.className = 'capture-grid';
  const iconButtons = new Map<PlayerIconId, HTMLButtonElement>();
  for (const icon of PLAYER_ICONS) {
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'capture-icon';
    btn.dataset.testid = `capture-icon-${icon.id}`;
    btn.dataset.active = icon.id === selectedIcon ? 'true' : 'false';
    btn.setAttribute('aria-label', icon.label);
    btn.textContent = icon.emoji;
    btn.addEventListener('click', () => {
      Audio.click();
      selectedIcon = icon.id;
      for (const [id, b] of iconButtons) {
        b.dataset.active = id === selectedIcon ? 'true' : 'false';
      }
    });
    iconButtons.set(icon.id, btn);
    grid.appendChild(btn);
  }
  modal.appendChild(grid);

  const nameRow = document.createElement('div');
  nameRow.className = 'capture-name-row';

  const nameLabel = document.createElement('span');
  nameLabel.className = 'capture-name-label';
  nameLabel.textContent = 'Name (optional):';
  nameRow.appendChild(nameLabel);

  const nameInput = document.createElement('input');
  nameInput.type = 'text';
  nameInput.className = 'capture-name-input';
  nameInput.maxLength = MAX_NAME_LENGTH;
  nameInput.placeholder = 'ABC';
  nameInput.dataset.testid = 'capture-name-input';
  nameInput.addEventListener('input', () => {
    const cleaned = nameInput.value
      .replace(NAME_FILTER_RE, '')
      .toUpperCase()
      .slice(0, MAX_NAME_LENGTH);
    if (nameInput.value !== cleaned) nameInput.value = cleaned;
    typedName = cleaned;
  });
  nameRow.appendChild(nameInput);
  modal.appendChild(nameRow);

  const buttons = document.createElement('div');
  buttons.className = 'capture-buttons';

  const saveBtn = document.createElement('button');
  saveBtn.type = 'button';
  saveBtn.className = 'chunky-btn teal';
  saveBtn.dataset.testid = 'capture-save-btn';
  saveBtn.textContent = '✓ SAVE!';
  saveBtn.addEventListener('click', () => {
    Audio.cheer();
    onSave({
      icon: selectedIcon,
      name: typedName.length > 0 ? typedName : null,
      score,
      hits,
      bestCombo,
    });
  });
  buttons.appendChild(saveBtn);

  const skipBtn = document.createElement('button');
  skipBtn.type = 'button';
  skipBtn.className = 'chunky-btn yellow small';
  skipBtn.dataset.testid = 'capture-skip-btn';
  skipBtn.textContent = 'Skip';
  skipBtn.addEventListener('click', () => onSkip());
  buttons.appendChild(skipBtn);

  modal.appendChild(buttons);
  root.appendChild(modal);

  return {
    element: root,
    destroy(): void {
      root.remove();
    },
  };
}
